<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  createLilithAssistant,
  deleteLilithAssistant,
  getLilithAssistants,
  updateLilithAssistant,
} from 'dashboard/api/lilithTeam';
import LilithOverview from './LilithOverview.vue';
import LilithFaqs from './LilithFaqs.vue';
import LilithDocuments from './LilithDocuments.vue';
import LilithPlayground from './LilithPlayground.vue';
import LilithInboxes from './LilithInboxes.vue';
import LilithTools from './LilithTools.vue';
import LilithSettings from './LilithSettings.vue';
import LilithScenarios from './LilithScenarios.vue';
import {
  getAvailableLilithSurfaces,
  resolveLilithSurface,
} from './lilithSurfaces';

const props = defineProps({
  surface: {
    type: String,
    default: 'overview',
  },
  assistantId: {
    type: String,
    default: '',
  },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const assistants = ref([]);
const assistantCapabilities = ref({ can_manage_assistants: false });
const assistantsLoading = ref(false);
const deleteAssistantArmed = ref(false);
const assistantForm = reactive({
  open: false,
  mode: 'create',
  id: '',
  name: '',
  description: '',
  productName: '',
  featureFaq: false,
  featureMemory: false,
  featureCitation: false,
});

const selectedAssistantId = computed(() => String(props.assistantId || ''));
const currentAssistant = computed(() =>
  assistants.value.find(item => String(item.id) === selectedAssistantId.value)
);
const canManageAssistants = computed(
  () => assistantCapabilities.value.can_manage_assistants === true
);
const isSurfaceFeatureEnabled = featureFlag =>
  Boolean(isFeatureEnabledonAccount.value(accountId.value, featureFlag));
const availableSurfaces = computed(() =>
  getAvailableLilithSurfaces(isSurfaceFeatureEnabled)
);

const surfaceLabels = computed(() => ({
  overview: t('TEAM_LILITH.NAV.OVERVIEW'),
  faqs: t('TEAM_LILITH.NAV.FAQS'),
  documents: t('TEAM_LILITH.NAV.DOCUMENTS'),
  scenarios: t('TEAM_LILITH.NAV.SCENARIOS'),
  playground: t('TEAM_LILITH.NAV.PLAYGROUND'),
  inboxes: t('TEAM_LILITH.NAV.INBOXES'),
  tools: t('TEAM_LILITH.NAV.TOOLS'),
  settings: t('TEAM_LILITH.NAV.SETTINGS'),
}));
const surfaceDescriptions = computed(() => ({
  overview: t('TEAM_LILITH.WORKSPACE.SURFACES.OVERVIEW'),
  faqs: t('TEAM_LILITH.WORKSPACE.SURFACES.FAQS'),
  documents: t('TEAM_LILITH.WORKSPACE.SURFACES.DOCUMENTS'),
  scenarios: t('TEAM_LILITH.WORKSPACE.SURFACES.SCENARIOS'),
  playground: t('TEAM_LILITH.WORKSPACE.SURFACES.PLAYGROUND'),
  inboxes: t('TEAM_LILITH.WORKSPACE.SURFACES.INBOXES'),
  tools: t('TEAM_LILITH.WORKSPACE.SURFACES.TOOLS'),
  settings: t('TEAM_LILITH.WORKSPACE.SURFACES.SETTINGS'),
}));

const currentSurface = computed(() =>
  resolveLilithSurface(props.surface, isSurfaceFeatureEnabled)
);
const currentSurfaceLabel = computed(() =>
  currentSurface.value ? surfaceLabels.value[currentSurface.value.key] : ''
);
const currentSurfaceDescription = computed(() =>
  currentSurface.value
    ? surfaceDescriptions.value[currentSurface.value.key]
    : ''
);
const currentStatusDescription = computed(() =>
  currentSurface.value?.status === 'partial'
    ? t('TEAM_LILITH.WORKSPACE.PARTIAL_DESCRIPTION')
    : t('TEAM_LILITH.WORKSPACE.PENDING_DESCRIPTION')
);
const statusLabel = surface =>
  surface.status === 'partial'
    ? t('TEAM_LILITH.WORKSPACE.STATUS_PARTIAL')
    : t('TEAM_LILITH.WORKSPACE.STATUS_PENDING');

const assistantRoute = (
  routeName,
  assistantId = selectedAssistantId.value
) => ({
  name: routeName,
  params: {
    accountId: route.params.accountId,
    assistantId,
  },
});
const surfaceRoute = surface => assistantRoute(surface.routeName);

const ensureAccessibleSurface = async () => {
  if (!currentAccount.value?.id) return false;
  if (!currentSurface.value) {
    await router.replace({
      name: 'home',
      params: { accountId: route.params.accountId },
    });
    return false;
  }
  if (selectedAssistantId.value && currentSurface.value.key !== props.surface) {
    await router.replace(surfaceRoute(currentSurface.value));
  }
  return true;
};

const loadAssistants = async () => {
  assistantsLoading.value = true;
  try {
    const response = await getLilithAssistants(accountId.value);
    assistants.value = response.data?.payload || [];
    assistantCapabilities.value =
      response.data?.capabilities || assistantCapabilities.value;
    if (
      currentSurface.value &&
      assistants.value.length &&
      !assistants.value.some(
        item => String(item.id) === selectedAssistantId.value
      )
    ) {
      await router.replace(
        assistantRoute(
          currentSurface.value.routeName,
          String(assistants.value[0].id)
        )
      );
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.WORKSPACE.ASSISTANT_ERROR')
    );
  } finally {
    assistantsLoading.value = false;
  }
};

const selectAssistant = event => {
  const assistantId = String(event.target.value || '');
  if (
    !currentSurface.value ||
    !assistantId ||
    assistantId === selectedAssistantId.value
  ) {
    return;
  }
  deleteAssistantArmed.value = false;
  router.push({
    ...assistantRoute(currentSurface.value.routeName, assistantId),
    query: {},
  });
};

const openCreateAssistant = () => {
  deleteAssistantArmed.value = false;
  Object.assign(assistantForm, {
    open: true,
    mode: 'create',
    id: '',
    name: '',
    description: '',
    productName: '',
    featureFaq: false,
    featureMemory: false,
    featureCitation: false,
  });
};

const openEditAssistant = () => {
  if (!currentAssistant.value) return;
  deleteAssistantArmed.value = false;
  Object.assign(assistantForm, {
    open: true,
    mode: 'edit',
    id: String(currentAssistant.value.id),
    name: currentAssistant.value.name || '',
    description: currentAssistant.value.description || '',
    productName: currentAssistant.value.config?.product_name || '',
    featureFaq: currentAssistant.value.config?.feature_faq === true,
    featureMemory: currentAssistant.value.config?.feature_memory === true,
    featureCitation: currentAssistant.value.config?.feature_citation === true,
  });
};

const closeAssistantForm = () => {
  assistantForm.open = false;
};

const saveAssistant = async () => {
  const name = assistantForm.name.trim();
  const description = assistantForm.description.trim();
  const productName = assistantForm.productName.trim();
  if (!name || !description || !productName || !canManageAssistants.value) {
    return;
  }
  try {
    const data = {
      name,
      description,
      config: {
        product_name: productName,
        feature_faq: assistantForm.featureFaq,
        feature_memory: assistantForm.featureMemory,
        feature_citation: assistantForm.featureCitation,
      },
    };
    const response =
      assistantForm.mode === 'create'
        ? await createLilithAssistant(accountId.value, data)
        : await updateLilithAssistant(accountId.value, assistantForm.id, data);
    assistantForm.open = false;
    await loadAssistants();
    const assistantId = String(response.data?.id || assistantForm.id || '');
    if (assistantId && currentSurface.value) {
      await router.push(
        assistantRoute(currentSurface.value.routeName, assistantId)
      );
    }
    useAlert(t('TEAM_LILITH.WORKSPACE.ASSISTANT_SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.WORKSPACE.ASSISTANT_ERROR')
    );
  }
};

const destroyAssistant = async () => {
  if (!currentAssistant.value || !canManageAssistants.value) return;
  if (!deleteAssistantArmed.value) {
    deleteAssistantArmed.value = true;
    return;
  }
  try {
    await deleteLilithAssistant(accountId.value, currentAssistant.value.id);
    await router.replace({
      name: 'lilith_assistants',
      params: { accountId: route.params.accountId },
    });
    await loadAssistants();
    deleteAssistantArmed.value = false;
    useAlert(t('TEAM_LILITH.WORKSPACE.ASSISTANT_DELETED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.WORKSPACE.ASSISTANT_ERROR')
    );
  }
};

const applyAssistantUpdate = updatedAssistant => {
  if (!updatedAssistant?.id) return;
  const index = assistants.value.findIndex(
    item => String(item.id) === String(updatedAssistant.id)
  );
  if (index === -1) return;
  assistants.value.splice(index, 1, {
    ...assistants.value[index],
    ...updatedAssistant,
  });
};

watch(
  () => [props.assistantId, props.surface],
  async ([assistantId]) => {
    if (assistantId || !assistants.value.length || !currentSurface.value) {
      return;
    }
    await router.replace(
      assistantRoute(
        currentSurface.value.routeName,
        String(assistants.value[0].id)
      )
    );
  }
);

watch(
  () => [
    props.surface,
    selectedAssistantId.value,
    availableSurfaces.value.map(surface => surface.key).join('|'),
  ],
  () => ensureAccessibleSurface()
);

watch(
  () => currentAccount.value?.id,
  async id => {
    if (id && (await ensureAccessibleSurface())) await loadAssistants();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
    <section
      class="shrink-0 border-b border-n-weak bg-n-surface-2 px-6 py-4 lg:px-10"
    >
      <div class="mx-auto flex w-full max-w-6xl flex-col gap-3">
        <div class="flex flex-wrap items-end justify-between gap-3">
          <label
            class="flex min-w-64 flex-col gap-1 text-xs font-medium text-n-slate-10"
          >
            {{ $t('TEAM_LILITH.WORKSPACE.ASSISTANT_LABEL') }}
            <select
              :value="selectedAssistantId"
              class="h-9 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12"
              :disabled="assistantsLoading || !assistants.length"
              @change="selectAssistant"
            >
              <option v-if="!assistants.length" value="">
                {{ $t('TEAM_LILITH.WORKSPACE.NO_ASSISTANTS') }}
              </option>
              <option
                v-for="assistant in assistants"
                :key="assistant.id"
                :value="assistant.id"
              >
                {{ assistant.name }}
              </option>
            </select>
          </label>
          <div v-if="canManageAssistants" class="flex flex-wrap gap-2">
            <Button
              :label="$t('TEAM_LILITH.EFFECT_RECOVERY.TITLE')"
              size="sm"
              variant="ghost"
              @click="
                router.push({
                  name: 'lilith_effect_recovery',
                  params: { accountId },
                })
              "
            />
            <Button
              v-if="currentAssistant"
              :label="$t('TEAM_LILITH.WORKSPACE.EDIT_ASSISTANT')"
              size="sm"
              variant="ghost"
              color="slate"
              @click="openEditAssistant"
            />
            <Button
              :label="$t('TEAM_LILITH.WORKSPACE.NEW_ASSISTANT')"
              size="sm"
              icon="i-lucide-plus"
              @click="openCreateAssistant"
            />
            <Button
              v-if="currentAssistant"
              :label="
                deleteAssistantArmed
                  ? $t('TEAM_LILITH.WORKSPACE.CONFIRM_DELETE_ASSISTANT')
                  : $t('TEAM_LILITH.WORKSPACE.DELETE_ASSISTANT')
              "
              size="sm"
              :variant="deleteAssistantArmed ? 'solid' : 'ghost'"
              color="ruby"
              @click="destroyAssistant"
            />
          </div>
        </div>

        <div
          v-if="assistantForm.open"
          class="grid gap-3 rounded-lg border border-n-weak bg-n-surface-1 p-4 md:grid-cols-2"
        >
          <Input
            v-model="assistantForm.name"
            :label="$t('TEAM_LILITH.WORKSPACE.ASSISTANT_NAME')"
          />
          <TextArea
            v-model="assistantForm.description"
            :label="$t('TEAM_LILITH.WORKSPACE.ASSISTANT_DESCRIPTION')"
            :max-length="500"
            auto-height
          />
          <Input
            v-model="assistantForm.productName"
            :label="$t('TEAM_LILITH.WORKSPACE.PRODUCT_NAME')"
          />
          <fieldset class="flex flex-col gap-2 md:col-span-2">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ $t('TEAM_LILITH.WORKSPACE.ASSISTANT_FEATURES') }}
            </legend>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="assistantForm.featureFaq" type="checkbox" />
              {{ $t('TEAM_LILITH.WORKSPACE.FEATURE_FAQ') }}
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="assistantForm.featureMemory" type="checkbox" />
              {{ $t('TEAM_LILITH.WORKSPACE.FEATURE_MEMORY') }}
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="assistantForm.featureCitation" type="checkbox" />
              {{ $t('TEAM_LILITH.WORKSPACE.FEATURE_CITATION') }}
            </label>
          </fieldset>
          <div class="flex justify-end gap-2 md:col-span-2">
            <Button
              :label="$t('TEAM_LILITH.WORKSPACE.CANCEL_ASSISTANT')"
              variant="ghost"
              color="slate"
              @click="closeAssistantForm"
            />
            <Button
              :label="$t('TEAM_LILITH.WORKSPACE.SAVE_ASSISTANT')"
              :disabled="
                !assistantForm.name.trim() ||
                !assistantForm.description.trim() ||
                !assistantForm.productName.trim()
              "
              @click="saveAssistant"
            />
          </div>
        </div>

        <nav v-if="currentAssistant" class="flex gap-1 overflow-x-auto pb-0.5">
          <router-link
            v-for="item in availableSurfaces"
            :key="item.key"
            :to="surfaceRoute(item)"
            :data-test="`surface-nav-${item.key}`"
            class="shrink-0 rounded-md px-3 py-1.5 text-sm text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
            active-class="bg-n-alpha-2 text-n-slate-12"
          >
            {{ surfaceLabels[item.key] }}
          </router-link>
        </nav>
      </div>
    </section>

    <LilithOverview
      v-if="currentAssistant && currentSurface.key === 'overview'"
      :assistant="currentAssistant"
      :can-drilldown="canManageAssistants"
    />
    <LilithFaqs
      v-else-if="currentAssistant && currentSurface.key === 'faqs'"
      :assistant-id="selectedAssistantId"
    />
    <LilithDocuments
      v-else-if="currentAssistant && currentSurface.key === 'documents'"
      :assistant-id="selectedAssistantId"
    />
    <LilithScenarios
      v-else-if="currentAssistant && currentSurface.key === 'scenarios'"
      :assistant-id="selectedAssistantId"
      :can-manage="canManageAssistants"
    />
    <LilithPlayground
      v-else-if="currentAssistant && currentSurface.key === 'playground'"
      :assistant-id="selectedAssistantId"
      :assistant="currentAssistant"
      :can-manage="canManageAssistants"
      @assistant-updated="loadAssistants"
    />
    <LilithInboxes
      v-else-if="currentAssistant && currentSurface.key === 'inboxes'"
      :assistant-id="selectedAssistantId"
      :can-manage="canManageAssistants"
    />
    <LilithTools
      v-else-if="currentAssistant && currentSurface.key === 'tools'"
      :can-manage="canManageAssistants"
    />
    <LilithSettings
      v-else-if="currentAssistant && currentSurface.key === 'settings'"
      :assistant="currentAssistant"
      :can-manage="canManageAssistants"
      @updated="applyAssistantUpdate"
    />
    <div
      v-else-if="currentAssistant"
      class="flex-1 overflow-y-auto bg-n-surface-1 px-6 py-8 lg:px-10"
    >
      <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <header class="flex flex-col gap-2">
          <div
            class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
          >
            {{ $t('TEAM_LILITH.WORKSPACE.EYEBROW') }}
          </div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ currentSurfaceLabel }}
          </h1>
          <p class="max-w-3xl text-sm leading-6 text-n-slate-11">
            {{ currentSurfaceDescription }}
          </p>
        </header>

        <div
          class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm leading-6 text-n-slate-11"
        >
          {{ $t('TEAM_LILITH.WORKSPACE.PARITY_NOTE') }}
        </div>

        <section class="rounded-xl border border-n-weak bg-n-surface-2 p-5">
          <div class="flex items-center justify-between gap-4">
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ $t('TEAM_LILITH.WORKSPACE.SECTION_STATUS') }}
            </h2>
            <span
              class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
            >
              {{ statusLabel(currentSurface) }}
            </span>
          </div>
          <p class="mt-3 text-sm leading-6 text-n-slate-11">
            {{ currentStatusDescription }}
          </p>
        </section>

        <section>
          <h2 class="mb-3 text-sm font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.WORKSPACE.PARITY_TITLE') }}
          </h2>
          <div class="grid gap-2 md:grid-cols-2">
            <router-link
              v-for="item in availableSurfaces"
              :key="item.key"
              :to="surfaceRoute(item)"
              :data-test="`surface-card-${item.key}`"
              class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-surface-2 px-4 py-3 transition-colors hover:bg-n-alpha-1"
            >
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-n-slate-12">
                  {{ surfaceLabels[item.key] }}
                </div>
                <div class="mt-0.5 truncate text-xs text-n-slate-10">
                  {{ surfaceDescriptions[item.key] }}
                </div>
              </div>
              <span class="shrink-0 text-xs text-n-slate-10">
                {{ statusLabel(item) }}
              </span>
            </router-link>
          </div>
        </section>
      </div>
    </div>
    <div
      v-else
      class="flex flex-1 items-center justify-center bg-n-surface-1 px-6 py-12"
    >
      <div class="max-w-lg text-center">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ $t('TEAM_LILITH.WORKSPACE.NO_ASSISTANTS') }}
        </h1>
        <p class="mt-2 text-sm leading-6 text-n-slate-10">
          {{ $t('TEAM_LILITH.WORKSPACE.NO_ASSISTANTS_DESCRIPTION') }}
        </p>
        <Button
          v-if="canManageAssistants"
          class="mt-4"
          :label="$t('TEAM_LILITH.WORKSPACE.CREATE_FIRST_ASSISTANT')"
          icon="i-lucide-plus"
          @click="openCreateAssistant"
        />
      </div>
    </div>
  </div>
</template>
